import Box from "@mui/material/Box";
import { DropdownOption } from "Interface/Dropdown";
import { GlobalUserInterface } from "Interface/interface";
import { ReactComponent as AddNew } from "assets/AddNew.svg";
import { clearAllCardSelection, setActiveActionCard } from "components/app/App.slice";
import Button from "components/common/Button";
import Chip from "components/common/Chip";
import DialogFormation from "components/common/DialogFormation";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import Dropdown from "components/common/dropdowns/DropDown";
import { useTranslate } from "hooks/useTranslate";
import { uniq } from "lodash";
import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { converter_ArrayObj, converter_ArrayString, getMoreValues, getTableColor, getUserHltId, getUserType } from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { checkTemplatePlanPayload, getPlanDetailsRoute, hasTemplatePlansAccess } from "utils/StoreUtils";
import { formTypes, userTypes } from "utils/Types";
import ActionCardDialogsContext from "../ActionCard/Context";
import { flowOptions, setCustomPlanCreationFlow, setPlanMode } from "../CustomPlanCreation/CustomPlanCreation.slice";
import { setCopySectionData } from "../planDetails/PlanDetail.slice";
import {
  copyActionAsync,
  copySectionAsync,
  fetchMyPlanDetailAsync,
  fetchMyPlansWithInncodesAsync,
  fetchOwnerDetailsByHtlIdsAsync,
  reset
} from "./CopyToPlan.slice";
import "./index.scss";

// Builds dropdown options combining plan name with the resolved owner's name, created date and status
// (owner details are resolved from the associates API response via CopyToPlan's own `ownersList`).
const buildPlanOptions = (plans: any[], ownerList: GlobalUserInterface[]): DropdownOption[] =>
  plans?.map((plan: any) => {
    const owners = ownerList?.filter((user) => plan?.owners?.includes(user?.hltId)) ?? [];
    const ownerNames = owners.map((owner) =>
      getUserNameWithConditionalCheck(owner?.displayName, owner?.firstName, owner?.lastName)
    );
    return {
      id: plan?.id,
      val: plan?.title,
      ownerName: ownerNames.length ? getMoreValues(ownerNames) : undefined,
      createdDate: plan?.createdDate,
      status: plan?.status
    };
  }) ?? [];

export const CopyToPlan = () => {
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const innCode = useAppSelector((state) => state.app.innCode);
  const propertyPlan = translate("common.propertyPlans");

  const isConsultant = useAppSelector((state) => state.app.loggedInUserDetails.isConsultant);
  const isImpersonated = useAppSelector((state) => state.app.impersonationDetails.isImpersonated);
  const activeCard = useAppSelector((state) => state.app.activeActionCard);
  const selectedCards = useAppSelector((state) => state?.app?.selectedCards);
  const copySectionData = useAppSelector((state) => state.planDetails.copySectionData);
  const { plansList, myPlanDetails, ownersList } = useAppSelector((state) => state.CopyToPlan);

  const [targetPlanId, setTargetPlanId] = useState<string>();
  const [targetSectionId, setTargetSectionId] = useState<string>();
  const [planOptions, setPlanOptions] = useState<DropdownOption[]>([]);

  const [inputValue, setInputValue] = useState<string>("");
  const [planType, setPlanType] = useState<string>(propertyPlan);
  const isMultipleCardsSelected = selectedCards?.cardList.length;

  const navigate = useNavigate();

  const {
    isOpenCopyActionDialog,
    setIsOpenCopyActionDialog,
    setIsOpenCustomPlanCreateDialog,
    setIsOpenViewActionDialog
  } = React.useContext(ActionCardDialogsContext);

  React.useEffect(() => {
    setPlanOptions(buildPlanOptions(plansList, ownersList));
  }, [plansList, ownersList]);

  React.useEffect(() => {
    const ownerHltIds = uniq(plansList?.flatMap((plan: any) => plan?.owners ?? []) ?? []);
    if (ownerHltIds.length) {
      dispatch(fetchOwnerDetailsByHtlIdsAsync({ value: ownerHltIds.join(","), size: ownerHltIds.length }));
    }
  }, [plansList]);

  React.useEffect(() => {
    isConsultant && onPlanTypeChange(translate("common.templatePlans"));
    getUserType() === userTypes.HOTEL_USER && onPlanTypeChange(propertyPlan);

    return () => {
      dispatch(reset());
      setTargetPlanId(undefined);
    };
  }, []);

  React.useEffect(() => {
    const searchTerm = inputValue?.toLowerCase();
    const filtredSearch = buildPlanOptions(plansList, ownersList).filter((item: DropdownOption) =>
      item?.val?.toLowerCase().includes(searchTerm)
    );
    setPlanOptions(filtredSearch);
  }, [inputValue]);

  const onPlanTypeChange = (val: string) => {
    dispatch(reset());

    if (val === propertyPlan) {
      setPlanType(propertyPlan);
      dispatch(
        fetchMyPlansWithInncodesAsync({
          planType: "PLAN",
          innCode: innCode,
          size: 100,
          searchedHltId: getUserHltId(),
          includeOwners: true
        })
      );
    } else {
      setPlanType(translate("common.templatePlans"));
      dispatch(
        fetchMyPlansWithInncodesAsync({
          planType: "TEMPLATE_PLAN",
          size: 100,
          searchedHltId: isImpersonated ? getUserHltId() : null,
          includeOwners: true
        })
      );
    }
  };

  const getSectionId = (val: string) => {
    setTargetSectionId(val);
  };

  const handleInputChange = (val: string) => {
    setInputValue(val);
  };

  const closeCopyDialog = () => {
    setIsOpenCopyActionDialog(false);
    setInputValue("");
  };

  const createPlanForm = () => {
    closeCopyDialog();
    if (planType === translate("common.templatePlans")) {
      dispatch(setPlanMode(formTypes.CreateTemplatePlan));
      dispatch(setCustomPlanCreationFlow(flowOptions["third"]));
    }
    setIsOpenCustomPlanCreateDialog(true);
    dispatch(reset());
  };

  const getPlanId = (val: string) => {
    if (val) {
      setIsOpenCopyActionDialog(true);
      setTargetPlanId(val);
      if (!copySectionData?.copySectionType) dispatch(fetchMyPlanDetailAsync({ id: val }));
    }
  };

  const handleCopyDialog = () => {
    dispatch(clearAllCardSelection());
    dispatch(setActiveActionCard({}));
    dispatch(setCopySectionData({}));
    closeCopyDialog();
    setIsOpenCustomPlanCreateDialog(false);
  };

  const copyToExistingPlan = async () => {
    let response;
    if (copySectionData?.copySectionType) {
      const props = {
        planId: targetPlanId,
        ...copySectionData.sectionPayload[0]
      };
      const isTemplatePlan = planType === translate("common.templatePlans");
      const payload = checkTemplatePlanPayload(props, isTemplatePlan);
      response = await dispatch(copySectionAsync(payload));
    } else if (isMultipleCardsSelected) {
      const cardsPayload = { cards: selectedCards?.cardList };
      const payload = checkTemplatePlanPayload(cardsPayload, true);
      const url = { planId: targetPlanId, sectionId: targetSectionId };
      await dispatch(copyActionAsync({ url, payload }));
    } else {
      const cards = [];
      cards.push(activeCard);
      const cardsPayload = { cards: cards };
      const payload = checkTemplatePlanPayload(cardsPayload, true);
      const url = { planId: targetPlanId, sectionId: targetSectionId };
      await dispatch(copyActionAsync({ url, payload }));
    }
    const sectionId = copySectionData?.copySectionType
      ? response?.payload?.sections[response?.payload?.sections?.length - 1]?.id
      : targetSectionId;

    const route = getPlanDetailsRoute(planType === translate("common.templatePlans"));
    navigate(`/${route}/${targetPlanId}/${sectionId}`);

    dispatch(
      setFeedbackDetails({
        shouldShow: true,
        variant: "success",
        setState: setFeedbackDetails({ shouldShow: false }),
        message: copySectionData?.copySectionType
          ? translate("myPlan.addSection.feedback")
          : translate("myPlan.importFromLibrary.feedback")
      })
    );

    closeCopyDialog();
    dispatch(setCopySectionData({}));
    setIsOpenViewActionDialog(false);
  };

  return (
    <>
      <DialogFormation
        title={copySectionData?.dialogTitle || translate("library.copyActionToMyPlan.popup.title")}
        openDialog={isOpenCopyActionDialog || false}
        handleClose={handleCopyDialog}
        actionConfig={
          <div className="modal-config-wrapper">
            <Button variant="secondary" label="Cancel" onClick={handleCopyDialog} />
            <Button
              accessType={accessTypes.update}
              variant="primary"
              label="Confirm"
              disabled={copySectionData?.copySectionType ? !targetPlanId : !targetSectionId}
              onClick={copyToExistingPlan}
            />
          </div>
        }
      >
        <div className="vertical-center display-col">
          <Box width="60%">
            {!isConsultant && hasTemplatePlansAccess() && (
              <Dropdown
                initialValue={isConsultant ? translate("common.templatePlans") : propertyPlan}
                handleChangeUpdate={onPlanTypeChange}
                options={converter_ArrayString([propertyPlan, translate("common.templatePlans")])}
                selectStatement="Select a plan type"
                sx={{ minWidth: "80%" }}
              />
            )}
            <Dropdown
              handleChangeUpdate={(val: string) => getPlanId(val)}
              options={planOptions}
              selectStatement="Select a plan"
              sx={{ minWidth: "80%" }}
              isInputEnabled={true}
              inputValue={inputValue}
              handleInputChange={handleInputChange}
              renderOption={(option: DropdownOption) => (
                <Box className="copy-to-plan-option">
                  <Typography variant="body2" showBold>
                    {option?.val}
                  </Typography>
                  <Box className="copy-to-plan-option__meta">
                    <div>
                      <Typography variant="label" color={tokens.Text.General.Tertiary}>
                        {option?.ownerName}
                      </Typography>
                    </div>
                    <Box className="copy-to-plan-option__status-row">
                      <Typography variant="label" color={tokens.Text.General.Tertiary}>
                        {getUIFormatDate(option?.createdDate)}
                      </Typography>
                      {option?.status && (
                        <Chip
                          sx={{ width: "fit-content" }}
                          label={option.status}
                          size="small"
                          color={getTableColor(option.status)}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            />
            {!copySectionData?.copySectionType && (
              <Dropdown
                handleChangeUpdate={(val: string) => getSectionId(val)}
                disabled={!targetPlanId}
                options={converter_ArrayObj(myPlanDetails?.sections, "name")}
                selectStatement="Select a section"
                sx={{ minWidth: "80%" }}
              />
            )}
            {copySectionData?.copySectionType && (
              <Typography
                className="text-center copy-to-plan-or-text"
                variant={"body2"}
                children={"or"}
              ></Typography>
            )}
            {
              <Button
                accessType={accessTypes.update}
                label={
                  planType === translate("common.templatePlans")
                    ? translate("custom.template.plan.creation.title")
                    : translate("custom.plan.creation.title")
                }
                width="100%"
                variant="secondary"
                startIcon={<AddNew />}
                onClick={createPlanForm}
                className="copy-to-plan-create-button"
              />
            }
          </Box>
        </div>
      </DialogFormation>
    </>
  );
};
